// MoodPresenter.js — comic-balloon UI for Mood Engine Phase 1 + 2
// Phase 1: individual speech bubbles
// Phase 2: sequential 2-line interactions between different characters

import moodData from '../data/mood_data.json';

const UI_CFG = moodData.config.ui;
const DEPTH  = 300;

export class MoodPresenter {
  constructor(scene) {
    this.scene    = scene;
    this._visible = false;
    this._objs    = [];
    this._timer   = null;
  }

  get isVisible() { return this._visible; }

  // ── Single line (Phase 1) ─────────────────────────────────────────────────
  show({ text, speaker, speakerId, color, x, y }) {
    this._destroy();
    this._buildBalloon({ text, speaker, color, x, y });
    this._visible = true;

    const lines    = this._countLines(text);
    const duration = lines > 1 ? UI_CFG.doubleLineDurationMs : UI_CFG.singleLineDurationMs;
    this._timer = this.scene.time.delayedCall(duration, () => this._fadeOut());
  }

  // ── Sequential interaction (Phase 2) ─────────────────────────────────────
  // lines: [{text, speaker, speakerId, color, x, y}, ...]
  showInteraction(lines) {
    this._destroy();
    this._showInteractionLine(lines, 0);
  }

  _showInteractionLine(lines, index) {
    if (index >= lines.length) return;
    const line = lines[index];

    this._destroy();
    this._buildBalloon({ text: line.text, speaker: line.speaker, color: line.color, x: line.x, y: line.y });
    this._visible = true;

    this._timer = this.scene.time.delayedCall(UI_CFG.interactionLineDurationMs, () => {
      this._fadeOut(() => this._showInteractionLine(lines, index + 1));
    });
  }

  // ── Build balloon graphics + text ─────────────────────────────────────────
  _buildBalloon({ text, speaker, color, x, y }) {
    const scene      = this.scene;
    const borderInt  = Phaser.Display.Color.HexStringToColor(color || '#334155').color;
    const maxW       = 168;
    const padX       = 10;
    const padY       = 14;
    const tailH      = 8;
    const charsPerLine = Math.floor((maxW - padX * 2) / 6.5);
    const lineCount    = Math.ceil(text.length / charsPerLine);
    const textH        = lineCount * 15;
    const boxW = maxW;
    const boxH = padY + textH + padY;
    const margin = 6;
    const bx = Phaser.Math.Clamp(x, boxW / 2 + margin, scene.scale.width - boxW / 2 - margin);
    const by = y;

    const gfx = scene.add.graphics().setDepth(DEPTH).setAlpha(0);

    gfx.fillStyle(0x0d1117, 0.93);
    gfx.fillRoundedRect(bx - boxW / 2, by - boxH / 2, boxW, boxH, 8);
    gfx.lineStyle(2, borderInt, 1);
    gfx.strokeRoundedRect(bx - boxW / 2, by - boxH / 2, boxW, boxH, 8);

    const tailX = bx;
    const tailY = by + boxH / 2;
    gfx.fillStyle(0x0d1117, 0.93);
    gfx.fillTriangle(tailX - 7, tailY, tailX + 7, tailY, tailX, tailY + tailH);
    gfx.lineStyle(2, borderInt, 1);
    gfx.strokeTriangle(tailX - 7, tailY, tailX + 7, tailY, tailX, tailY + tailH);

    const dialogText = scene.add.text(bx, by - boxH / 2 + padY, text, {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#e2e8f0',
      wordWrap: { width: boxW - padX * 2 }, lineSpacing: 2,
    }).setOrigin(0.5, 0).setDepth(DEPTH + 1).setAlpha(0);

    this._objs = [gfx, dialogText];

    scene.tweens.add({
      targets: this._objs, alpha: 1, y: `-=6`,
      duration: UI_CFG.fadeInMs, ease: 'Sine.Out',
    });
  }

  _countLines(text) {
    const charsPerLine = Math.floor((168 - 20) / 6.5);
    return Math.ceil(text.length / charsPerLine);
  }

  // speaker param kept for API compatibility but no longer rendered


  // ── Fade out, then optional callback ─────────────────────────────────────
  _fadeOut(onComplete) {
    if (this._objs.length === 0) { onComplete?.(); return; }
    this.scene.tweens.add({
      targets: this._objs, alpha: 0,
      duration: UI_CFG.fadeOutMs, ease: 'Sine.In',
      onComplete: () => { this._destroy(); onComplete?.(); },
    });
  }

  _destroy() {
    if (this._timer) { this._timer.remove(); this._timer = null; }
    for (const o of this._objs) { try { o?.destroy(); } catch (_) {} }
    this._objs    = [];
    this._visible = false;
  }
}
